import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ActivationPage from './pages/ActivationPage';
import PasswordResetRequestPage from './pages/PasswordResetRequestPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import LandingPage from './pages/LandingPage';
import AppLayout from './components/AppLayout';
import AccountsPage from './pages/AccountsPage';
import MyStrategiesPage from './pages/MyStrategiesPage';
import StrategyScreenerPage from './pages/StrategyScreenerPage';
import ProfilePage from './pages/ProfilePage';
import AccountDetailsPage from './pages/AccountDetailsPage';
import TradeDetailsPage from './pages/TradeDetailsPage';
import StrategyDetailsPage from './pages/StrategyDetailsPage';

const DEFAULT_AUTH_REDIRECT = '/my/accounts';

function sanitizeInternalPath(candidate) {
    if (typeof candidate !== 'string') return DEFAULT_AUTH_REDIRECT;
    const raw = candidate.trim();
    if (!raw) return DEFAULT_AUTH_REDIRECT;
    if (!raw.startsWith('/')) return DEFAULT_AUTH_REDIRECT;
    if (raw.startsWith('//')) return DEFAULT_AUTH_REDIRECT;

    let decoded = raw;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        decoded = raw;
    }

    // reject absolute/protocol-like values
    if (/^\/?[a-zA-Z][a-zA-Z\d+\-.]*:/.test(decoded)) return DEFAULT_AUTH_REDIRECT;
    if (decoded.startsWith('/login')) return DEFAULT_AUTH_REDIRECT;

    return decoded;
}

function RequireAuth({ children }) {
    const { isAuthenticated, initializing } = useAuth();
    const location = useLocation();

    if (initializing) return null;

    if (!isAuthenticated) {
        const requested = `${location.pathname}${location.search}${location.hash}`;
        const from = sanitizeInternalPath(requested);
        return <Navigate to="/login" replace state={{ from }} />;
    }

    return children;
}

function PublicOnly({ children }) {
    const { isAuthenticated, initializing } = useAuth();

    if (initializing) return null;

    if (isAuthenticated) {
        return <Navigate to="/my/accounts" replace />;
    }

    return children;
}

// todo: more strategies by this user
// todo: trader profile

function AppContent() {
    return (
        <>
            <Routes>
                <Route
                    path="/"
                    element={<LandingPage />}
                />
                <Route path="/screener" element={<StrategyScreenerPage />} />
                <Route
                    path="/login"
                    element={
                        <PublicOnly>
                            <LoginPage />
                        </PublicOnly>
                    }
                />
                <Route
                    path="/signup"
                    element={
                        <PublicOnly>
                            <SignupPage />
                        </PublicOnly>
                    }
                />
                <Route path="/forgot-password" element={<PasswordResetRequestPage />} />
                <Route path="/password/reset/confirm/:uid/:token" element={<PasswordResetConfirmPage />} />
                <Route path="/activate/:uid/:token" element={<ActivationPage />} />

                <Route
                    element={
                        <RequireAuth>
                            <AppLayout />
                        </RequireAuth>
                    }
                >
                    <Route path="/my/accounts" element={<AccountsPage />} />
                    <Route path="/my/accounts/:number" element={<AccountDetailsPage />} />
                    <Route path="/my/accounts/:number/trades/:tradeId" element={<TradeDetailsPage />} />

                    <Route path="/my/strategies" element={<MyStrategiesPage />} />
                    <Route path="/strategies/:strategyId" element={<StrategyDetailsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />

                    <Route path="*" element={<Navigate to="/my/accounts" replace />} />
                </Route>
            </Routes>
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <div className="App">
                    <AppContent />
                </div>
            </Router>
        </AuthProvider>
    );
}

export default App;
